/**
 * Application shell: routing, responsive layout, and authenticated navigation.
 *
 * Owns the single {@link ApiClient}, presents the Passcode entry interface
 * whenever there is no active Session (Req 1.6) — including when any protected
 * request returns 401 — and, once authenticated, renders a mobile-first shell
 * with a header (sign-out) and bottom navigation that routes between the
 * dashboard, cost/yield entry, price, and price-target views. A hash router
 * keeps everything client-side so the app is a pure static bundle (design:
 * Frontend). Sign-out terminates the session and returns to Passcode entry
 * (Req 1.7).
 */
import { ApiClient } from "./api/client";
import {
  CROPS,
  cropLabel,
  type Crop,
} from "./config";
import { currentRoute, navigate, onRouteChange } from "./router";
import { clear, el, render } from "./ui/dom";
import { hasSession } from "./session";
import { passcodeView } from "./views/passcode";
import { dashboardView } from "./views/dashboard";
import { cropFormView } from "./views/crop-form";
import { priceView } from "./views/price";
import { targetsView } from "./views/targets";

/** The crop-scoped sections of the app. */
type Section = "dashboard" | "crops" | "price" | "targets";

/** Start the SPA, mounting into `#app`. */
export function startApp(mount: HTMLElement): void {
  const api = new ApiClient();

  // A 401 on any protected route clears the session and returns to Passcode
  // entry (Req 1.6). Routing to the root triggers a re-render that shows it.
  api.onUnauthorized = () => {
    navigate("/");
  };

  // Track the crop currently in focus for crop-scoped sections.
  let currentCrop: Crop = "corn";

  const routeAndRender = (): void => {
    if (!hasSession()) {
      renderUnauthenticated();
      return;
    }
    renderAuthenticated();
  };

  function renderUnauthenticated(): void {
    render(
      mount,
      passcodeView(api, () => {
        // On success, land on the dashboard.
        navigate("/dashboard");
        routeAndRender();
      })
    );
  }

  function renderAuthenticated(): void {
    const route = currentRoute();
    const [head, cropSeg] = route.segments;

    const section: Section =
      head === "crops" ? "crops" : head === "price" ? "price" : head === "targets" ? "targets" : "dashboard";

    // Sync the active crop from a crop-scoped URL when present.
    if (cropSeg === "corn" || cropSeg === "soybeans") {
      currentCrop = cropSeg;
    }

    const content = el("main", { class: "app-content" });
    render(content, viewForSection(section, currentCrop));

    render(
      mount,
      el("div", { class: "app-shell" }, [
        header(),
        content,
        nav(section),
      ])
    );
  }

  function viewForSection(section: Section, crop: Crop): HTMLElement {
    switch (section) {
      case "crops":
        return cropFormView(api, crop);
      case "price":
        return priceView(api, crop);
      case "targets":
        return targetsView(api, crop);
      case "dashboard":
      default:
        return dashboardView(api);
    }
  }

  function header(): HTMLElement {
    const signOut = el("button", {
      type: "button",
      class: "btn btn-ghost",
      text: "Sign out",
      on: {
        click: () => {
          void (async () => {
            await api.logout();
            navigate("/");
            routeAndRender();
          })();
        },
      },
    });

    return el("header", { class: "app-header" }, [
      el("span", { class: "app-title-sm", text: "Grain Breakeven" }),
      cropToggle(),
      signOut,
    ]);
  }

  /** Corn / soybeans toggle that re-routes the current crop-scoped section. */
  function cropToggle(): HTMLElement {
    const route = currentRoute();
    const [head] = route.segments;
    const section: Section =
      head === "crops" ? "crops" : head === "price" ? "price" : head === "targets" ? "targets" : "dashboard";

    const buttons = CROPS.map((crop) =>
      el("button", {
        type: "button",
        class: `crop-toggle-btn${crop === currentCrop ? " is-active" : ""}`,
        text: cropLabel(crop),
        attrs: { "aria-pressed": String(crop === currentCrop) },
        on: {
          click: () => {
            currentCrop = crop;
            if (section === "dashboard") {
              // The dashboard is not crop-scoped; just refresh the toggle state.
              routeAndRender();
            } else {
              navigate(`/${section}/${crop}`);
            }
          },
        },
      })
    );
    return el("div", { class: "crop-toggle", attrs: { role: "group", "aria-label": "Crop" } }, buttons);
  }

  function nav(active: Section): HTMLElement {
    const items: { section: Section; label: string; href: string }[] = [
      { section: "dashboard", label: "Dashboard", href: "/dashboard" },
      { section: "crops", label: "Costs", href: `/crops/${currentCrop}` },
      { section: "price", label: "Price", href: `/price/${currentCrop}` },
      { section: "targets", label: "Targets", href: `/targets/${currentCrop}` },
    ];

    return el(
      "nav",
      { class: "app-nav", attrs: { "aria-label": "Primary" } },
      items.map((item) =>
        el("button", {
          type: "button",
          class: `nav-btn${item.section === active ? " is-active" : ""}`,
          text: item.label,
          attrs: { "aria-current": item.section === active ? "page" : "false" },
          on: {
            click: () => navigate(item.href),
          },
        })
      )
    );
  }

  // Wire route changes and render the initial screen.
  onRouteChange(routeAndRender);
  clear(mount);
  routeAndRender();
}
